import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bot, Copy, Check, Loader2, Trash2, Plug } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { useToast } from '../components/ui/use-toast'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { aiIntegrationService, type ConnectedAiClient } from '../services/ai-integration'

export default function AiIntegrationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [connections, setConnections] = useState<ConnectedAiClient[]>([])
  const [loading, setLoading] = useState(true)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const connectorUrl = aiIntegrationService.connectorUrl()

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [{ label: t('aiIntegration.title') }],
    }),
    [t, navigate]
  )

  const loadConnections = useCallback(() => {
    setLoading(true)
    aiIntegrationService
      .listConnections()
      .then(setConnections)
      .catch(() => toast({ title: t('aiIntegration.loadError'), variant: 'destructive' }))
      .finally(() => setLoading(false))
  }, [t, toast])

  useEffect(() => {
    loadConnections()
  }, [loadConnections])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(connectorUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({ title: t('aiIntegration.copyError'), variant: 'destructive' })
    }
  }

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    try {
      await aiIntegrationService.revokeConnection(id)
      setConnections((prev) => prev.filter((c) => c.id !== id))
      toast({ title: t('aiIntegration.revoked') })
    } catch {
      toast({ title: t('aiIntegration.revokeError'), variant: 'destructive' })
    } finally {
      setRevoking(null)
    }
  }

  const claudeSteps = t('aiIntegration.claudeSteps', { returnObjects: true }) as string[]
  const chatgptSteps = t('aiIntegration.chatgptSteps', { returnObjects: true }) as string[]

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            {t('aiIntegration.title')}
          </CardTitle>
          <CardDescription>{t('aiIntegration.intro')}</CardDescription>
        </CardHeader>
      </Card>

      {/* Connector URL */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('aiIntegration.connectorUrlTitle')}</CardTitle>
          <CardDescription>{t('aiIntegration.connectorUrlDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input value={connectorUrl} readOnly className="font-mono text-sm" aria-label={t('aiIntegration.connectorUrlTitle')} />
            <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-2">{copied ? t('aiIntegration.copied') : t('aiIntegration.copy')}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How to connect */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('aiIntegration.howToTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="font-medium mb-2">{t('aiIntegration.claudeTitle')}</p>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              {claudeSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
          <div>
            <p className="font-medium mb-2">{t('aiIntegration.chatgptTitle')}</p>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              {chatgptSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Connected clients */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('aiIntegration.connectedTitle')}</CardTitle>
          <CardDescription>{t('aiIntegration.connectedDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : connections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center text-muted-foreground">
              <Plug className="h-6 w-6 mb-2" />
              <p className="text-sm">{t('aiIntegration.noConnections')}</p>
            </div>
          ) : (
            <ul className="divide-y">
              {connections.map((connection) => (
                <li key={connection.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {connection.applicationName || t('aiIntegration.unknownClient')}
                    </p>
                    {connection.connectedAt && (
                      <p className="text-xs text-muted-foreground">
                        {t('aiIntegration.connectedOn', {
                          date: new Date(connection.connectedAt).toLocaleDateString(),
                        })}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevoke(connection.id)}
                    disabled={revoking === connection.id}
                    className="text-destructive hover:text-destructive shrink-0"
                  >
                    {revoking === connection.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    <span className="ml-2">{t('aiIntegration.revoke')}</span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
